/**
 * BestPracticesSection - Bottom section showing proven approaches to solve the issue
 * Question: "What has worked before? What should we do?"
 */

import React from 'react';
import { Award, Gauge, Clock, CheckSquare, ArrowRight, Sparkles } from 'lucide-react';
import { RelatedItemCard, NODE_COLORS } from './RelatedItemCard';
import type { RelatedBestPractice } from './hooks/useIssueRelationships';

interface BestPracticesSectionProps {
  practices: RelatedBestPractice[];
  onPracticeClick?: (practiceId: string) => void;
}

// Complexity badge styles
const COMPLEXITY_STYLES: Record<string, { color: string; label: string }> = {
  low: { color: 'bg-green-100 text-green-700', label: 'Simple' },
  medium: { color: 'bg-yellow-100 text-yellow-700', label: 'Medium' },
  high: { color: 'bg-orange-100 text-orange-700', label: 'Complex' },
  very_high: { color: 'bg-red-100 text-red-700', label: 'Very Complex' },
};

export const BestPracticesSection: React.FC<BestPracticesSectionProps> = ({
  practices,
  onPracticeClick,
}) => {
  const colors = NODE_COLORS.BestPractice;

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden`}>
      {/* Header */}
      <div className="px-4 py-3 bg-white/60 border-b border-green-100">
        <div className="flex items-center gap-2">
          <Award className={`w-5 h-5 ${colors.accent}`} />
          <h3 className={`font-semibold ${colors.accent}`}>Solve: Best Practices</h3>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          Proven methods to address this issue
        </p>
      </div>

      {/* Content */}
      <div className="p-4">
        {practices.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {practices.map((item) => (
              <RelatedItemCard
                key={item.practice.practice_id}
                title={item.practice.title}
                description={item.practice.description}
                type="BestPractice"
                relationship={item.relationship}
                accentColor={colors.accent}
                borderColor={colors.border}
                bgColor="bg-white"
              >
                <div className="space-y-2">
                  {/* Complexity and Effort */}
                  <div className="flex flex-wrap gap-2">
                    {item.practice.complexity && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          COMPLEXITY_STYLES[item.practice.complexity]?.color ||
                          'bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        <Gauge className="w-3 h-3 inline mr-1" />
                        {COMPLEXITY_STYLES[item.practice.complexity]?.label ||
                          item.practice.complexity}
                      </span>
                    )}
                    {item.practice.estimated_effort && (
                      <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-600">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {item.practice.estimated_effort}
                      </span>
                    )}
                  </div>

                  {/* Expected Benefits Preview */}
                  {item.practice.expected_benefits &&
                    item.practice.expected_benefits.length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                          <Sparkles className="w-3 h-3" />
                          Expected Benefits:
                        </div>
                        <ul className="space-y-0.5 pl-4">
                          {item.practice.expected_benefits.slice(0, 2).map((benefit, index) => (
                            <li
                              key={index}
                              className="text-xs text-neutral-600 list-disc marker:text-green-400"
                            >
                              {benefit}
                            </li>
                          ))}
                          {item.practice.expected_benefits.length > 2 && (
                            <li className="text-xs text-neutral-400">
                              +{item.practice.expected_benefits.length - 2} more...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                  {/* Implementation Steps Preview */}
                  {item.practice.implementation_steps &&
                    item.practice.implementation_steps.length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs font-medium text-neutral-600">
                          <CheckSquare className="w-3 h-3" />
                          Implementation Steps:
                        </div>
                        <ul className="space-y-0.5 pl-4">
                          {item.practice.implementation_steps.slice(0, 2).map((step, index) => (
                            <li
                              key={index}
                              className="text-xs text-neutral-600 list-decimal marker:text-neutral-400"
                            >
                              {step}
                            </li>
                          ))}
                          {item.practice.implementation_steps.length > 2 && (
                            <li className="text-xs text-neutral-400">
                              +{item.practice.implementation_steps.length - 2} more steps...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              </RelatedItemCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="text-center py-6">
    <Award className="w-10 h-10 text-green-200 mx-auto mb-2" />
    <p className="text-sm text-neutral-500">No best practices linked yet</p>
    <p className="text-xs text-neutral-400 mt-1">
      Best practices provide proven solutions
    </p>
  </div>
);

export default BestPracticesSection;
